import config from 'configAlias';
import entries from 'lodash/entries';
import get from 'lodash/get';
import indexOf from 'lodash/indexOf';
import isArray from 'lodash/isArray';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';
import keys from 'lodash/keys';
import template from 'lodash/template';
import values from 'lodash/values';
import zipObject from 'lodash/zipObject';

const firstDepositTransactionType = require(`./../../../../../config/_reference/types/first-deposit-transaction-type.json`);
const firstDepositTypes = require(`./../../../../../config/en-SG/types/first-deposit-types.json`);

export const extendKeyPath = (keyPath, segment) => {
  const getUniqueName = (name, increment = 2) => {
    if (!(name in keyPath)) return name;
    if (!(name + increment in keyPath)) return name + increment;
    return getUniqueName(name, increment + 1);
  };

  const uniqueNames = keys(segment).map(name => getUniqueName(name));
  return { ...keyPath, ...zipObject(uniqueNames, values(segment)) };
};

export const transformKeyPath = keyPath => {
  if (isPlainObject(keyPath)) return keyPath;

  return (function recurse(path) {
    if (isString(path)) return recurse(path.split('.'));

    return path.reduce((acc, value, index) => {
      let key = !index
        ? 'pageName'
        : /^[0-9]+$/.test(value)
        ? 'index'
        : 'propertyName';

      return extendKeyPath(acc, { [key]: value });
    }, {});
  })(keyPath);
};

export const removeIndexFromKeyPath = keyPath => {
  if (!isPlainObject(keyPath))
    throw new Error('keyPath must be a plain object.');

  return entries(keyPath).reduce((acc, [key, value]) => {
    if (/^index(?:\d+)?$/.test(key) || !value) return acc;
    return [...acc, value];
  }, []);
};

export const resolvePatternCondition = (
  condition = {},
  _action,
  formValues
) => {
  if (condition.pattern) {
    const regex = new RegExp(condition.pattern);

    return regex.test(formValues);
  }

  return true;
};

export const resolveEqualityCondition = (
  condition = {},
  action,
  formValues
) => {
  if (condition.equality) {
    return (function recurse(condition, isRecursion = false, isExact = false) {
      let node = condition.anyOf || condition.exact || null;

      if (node) {
        let ary = isArray(node) ? node : entries(node);
        return condition.anyOf
          ? ary.some(item => recurse(item, true) === true)
          : ary.every(item => recurse(item, true, true) === true);
      }

      let conditionAry;

      if (isPlainObject(condition)) {
        conditionAry = entries(condition);
      } else if (isRecursion) {
        conditionAry = [condition];
      } else {
        conditionAry = condition;
      }

      return conditionAry.every(([keyPath, value]) => {
        const valueAtKeyPath = get(
          {
            action,
            formValues,
          },
          keyPath,
          null
        );

        const compareValue = isArray(value) ? value : [value];

        const compareTo = isArray(valueAtKeyPath)
          ? valueAtKeyPath
          : [valueAtKeyPath];

        if (isExact) {
          return (
            compareValue.length === compareTo.length &&
            compareValue.every(item => compareTo.includes(item) === true)
          );
        } else {
          return compareValue.some(item => compareTo.includes(item) === true);
        }
      });
    })(condition.equality);
  }

  return true;
};

export const resolvePresenceCondition = (
  condition = {},
  action,
  formValues
) => {
  if (condition.presence) {
    return (function recurse(condition) {
      if (condition.anyOf && isArray(condition.anyOf)) {
        return condition.anyOf.some(item => recurse(item) === true);
      }

      if (isArray(condition)) {
        return condition.every(
          keyPath =>
            get({ action, formValues }, keyPath, undefined) !== undefined
        );
      }

      return true;
    })(condition.presence);
  }

  return true;
};

export const resolveConditions = (
  eventProperties = {},
  action,
  formValues,
  conditions = [
    resolvePatternCondition,
    resolveEqualityCondition,
    resolvePresenceCondition,
  ]
) =>
  (function recurse(eventProperties) {
    if (eventProperties.oneOf) {
      if (isArray(eventProperties.oneOf)) {
        for (const subEventProperties of eventProperties.oneOf) {
          const subEvent = recurse(subEventProperties);

          if (subEvent.properties) {
            return subEvent;
          }
        }
      }

      return {};
    }

    if (eventProperties.if) {
      return conditions.every(condition =>
        condition(eventProperties.if, action, formValues)
      )
        ? eventProperties.then || {}
        : eventProperties.else || {};
    }

    return eventProperties;
  })(eventProperties);

export const combineProperties = eventProperties => {
  const { default: defaultProperties } = config.analytics || {};

  return {
    ...defaultProperties,
    ...eventProperties,
  };
};

const labelFetch = (value, enumSource, translations, labels) => {
  let label = '';
  let enumKeys = '';

  switch (enumSource) {
    case 'firstDepositTransactionType':
      enumKeys = firstDepositTransactionType;
      break;

    case 'firstDepositTypes':
      enumKeys = firstDepositTypes;
      break;

    default:
      enumKeys = '';
  }

  if (enumKeys !== '') {
    const getLabels = get(translations, labels, []);
    const index = indexOf(enumKeys, value);
    label = getLabels[index];
  }

  return label;
};

export const interpolateProperties = (combinedProperties, options) => {
  const properties = JSON.stringify(combinedProperties);

  const interpolatedProperties = template(properties, {
    imports: {
      _: { get, labelFetch },
    },
  })({ config, ...options });

  return JSON.parse(interpolatedProperties);
};

export const getPropertiesAtKeyPath = (action, properties) => {
  const transformedKeyPath = transformKeyPath(action.keyPath);

  const eventPropertiesAtKeyPath = get(
    properties,
    removeIndexFromKeyPath(transformedKeyPath),
    null
  );

  if (eventPropertiesAtKeyPath) {
    const formValuesAtKeyPath =
      action.type === 'onNavigate'
        ? get(action.values, values(transformedKeyPath), null)
        : action.type === 'onChange'
        ? action.value
        : get(action, 'value', null);

    if (
      eventPropertiesAtKeyPath.properties ||
      eventPropertiesAtKeyPath.oneOf ||
      eventPropertiesAtKeyPath.if
    ) {
      const eventProperties = resolveConditions(
        eventPropertiesAtKeyPath,
        action,
        formValuesAtKeyPath
      );

      if (eventProperties.properties) {
        const combinedProperties = combineProperties(
          eventProperties.properties
        );

        return {
          formValuesAtKeyPath,
          eventPropertiesAtKeyPath: interpolateProperties(combinedProperties, {
            action: {
              ...action,
              keyPath: transformedKeyPath,
            },
            value: formValuesAtKeyPath,
          }),
        };
      }

      return {};
    }

    return { formValuesAtKeyPath };
  }

  return {};
};
